// SSO Section Component
// PBI 759: SSO buttons section for AuthPage

import React, { useEffect, useState } from 'react';
import SSOButton from './SSOButton';
import { useSSO } from '../../hooks/useSSO';
import { clearAllAuthCookies, clearMSALCookies } from '../../utils/clearCookies';

interface SSOSectionProps {
  disabled?: boolean;
  mode?: 'login' | 'signup';
}

interface SSOConfig {
  google: { enabled: boolean; clientId: string };
}

export const SSOSection: React.FC<SSOSectionProps> = ({ disabled = false, mode = 'login' }) => {
  const { ssoState, authenticateWithSSO, getSSOConfig, clearSSOError } = useSSO();
  const [config, setConfig] = useState<SSOConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);

  // Load SSO configuration on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const ssoConfig = await getSSOConfig();
        setConfig(ssoConfig);
      } catch (error) {
        // Silently handle SSO config errors
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfig();
  }, [getSSOConfig]);

  // Handle SSO button click
  const handleSSOClick = (provider: 'google', forceAccountSelection: boolean = false) => {
    clearSSOError(provider);
    
    // Clear ALL auth cookies and MSAL cache before SSO
    clearAllAuthCookies();
    clearMSALCookies();
    
    // Clear any existing auth state that might interfere
    const currentHostname = window.location.hostname;
    
    // Clear for current domain
    document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT';
    
    // Clear for parent domain if applicable
    if (currentHostname.includes('talavera.ai')) {
      document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT; domain=.talavera.ai';
    } else if (currentHostname === 'localhost') {
      document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT; domain=localhost';
    }
    localStorage.removeItem('sso_return_url');
    localStorage.removeItem('redirectAfterLogin');
    
    authenticateWithSSO(provider, forceAccountSelection);
  };

  // Loading state
  if (configLoading) {
    return (
      <div className="space-y-3">
        <div className="text-center text-sm text-gray-500">
          <div className="animate-pulse">Loading authentication options...</div>
        </div>
      </div>
    );
  }

  // No SSO providers available
  if (!config || !config.google.enabled) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">Or continue with</span>
        </div>
      </div>
      
      <div className="space-y-2">
        {config.google.enabled && (
          <div className="space-y-2">
            <SSOButton
              provider="google"
              onClick={() => handleSSOClick('google')}
              disabled={disabled}
              loading={ssoState.google.loading}
            />
            
            {/* Only show account selection for login mode */}
            {mode === 'login' && (
              <button
                type="button"
                onClick={() => handleSSOClick('google', true)}
                disabled={disabled || ssoState.google.loading}
                className="w-full flex items-center justify-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Use different Google account
              </button>
            )}
            
            {ssoState.google.error && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="text-sm text-red-600">
                  <strong>Authentication Error:</strong> {ssoState.google.error}
                </div>
                <button
                  onClick={() => clearSSOError('google')}
                  className="mt-1 text-xs text-red-500 hover:text-red-700 underline"
                >
                  Try again
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SSOSection;